import { gql, type TypedDocumentNode } from '@apollo/client';

import type {
  CompanyRegistration,
  CompanyRegistrationList,
  CompanyRegistrationStatus,
} from '../types/companyRegistration.types';

const REGISTRATION_FIELDS = `
  id
  companyName
  companyCode
  industry
  size
  website
  country
  timezone
  applicantFirstName
  applicantLastName
  corporateEmail
  status
  emailVerifiedAt
  approvalComments
  approvedOn
  rejectedOn
  companyId
  createdAt
  updatedAt
`;

/** The Super Admin review table — offset paged, filterable by workflow status. */
export const COMPANY_REGISTRATIONS_QUERY: TypedDocumentNode<
  { companyRegistrations: CompanyRegistrationList | null },
  {
    page: number;
    pageSize: number;
    statuses?: CompanyRegistrationStatus[];
    search?: string;
    sortBy?: string | null;
    sortDescending?: boolean;
  }
> = gql`
  query CompanyRegistrations(
    $page: Int!
    $pageSize: Int!
    $statuses: [CompanyRegistrationStatus!]
    $search: String
    $sortBy: String
    $sortDescending: Boolean
  ) {
    companyRegistrations(
      page: $page
      pageSize: $pageSize
      statuses: $statuses
      search: $search
      sortBy: $sortBy
      sortDescending: $sortDescending
    ) {
      items { ${REGISTRATION_FIELDS} }
      totalCount
      page
      pageSize
      totalPages
      hasNextPage
      hasPreviousPage
    }
  }
`;

/** One registration for the review detail (Super Admin, or the applicant viewing their own). */
/**
 * The signed-in applicant's latest registration, driving the company portal gate. Adds
 * `companyStillExists` to the shared field list; the Super Admin grid query deliberately omits it,
 * since a Completed-but-later-deleted company is an audit fact there rather than a gating signal
 * (see `CompanyRegistrationProjections.Row`).
 */
export const MY_COMPANY_REGISTRATION_QUERY: TypedDocumentNode<{
  myCompanyRegistration: CompanyRegistration | null;
}> = gql`
  query MyCompanyRegistration {
    myCompanyRegistration {
      ${REGISTRATION_FIELDS}
      companyStillExists
    }
  }
`;
